import {
  Avatar,
  Box,
  Chip,
  ClickAwayListener,
  Divider,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  Popper,
  Stack,
  Typography
} from '@mui/material';
// assets
import { IconLogout, IconUser, IconUserCircle, IconUserX, IconReport, IconArrowBack } from '@tabler/icons';
import { fetchUserDetails, logoutUser } from '../../../../redux/user/userActions';
import { useEffect, useRef, useState } from 'react';
// project imports
import MainCard from 'ui-component/cards/MainCard';
// third-party
import PerfectScrollbar from 'react-perfect-scrollbar';
import Transitions from 'ui-component/extended/Transitions';
import ScheduleReport from 'components/Scheduler/MailerList';
import { connect } from 'react-redux';
import { fetchUserRoleByEmailId, fetchAllUserList } from '../../../../redux/advertiser/advertiserActions';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
// material-ui
import { useTheme } from '@mui/material/styles';

// ==============================|| PROFILE MENU ||============================== //

const ProfileSection = ({
  email,
  userName,
  fetchUserDetails,
  authorized,
  login,
  logoutUser,
  user_role,
  timeZone,
  fetchUserRoleByEmailId,
  fetchAllUserList,
  allUserList
}) => {
  const theme = useTheme();
  const customization = useSelector((state) => state.customization);
  const navigate = useNavigate();
  const [showClientOnboard, setShowClientOnboard] = useState(false);
  const [showSchedulerReport, setShowSchedulerReport] = useState(false);
  const [notification, setNotification] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    // console.log('ProfileSection mounted - fetching user details and user list');
    fetchUserDetails();
    fetchAllUserList();
  }, []);

  // Force re-render when allUserList changes
  useEffect(() => {
    // console.log('allUserList changed, length:', allUserList?.length || 0);
    // console.log('allUserList full data:', allUserList);
    // Also log the Redux state path to make sure we're getting the right data
    // console.log('Redux state path check - allUserList:', allUserList);
  }, [allUserList]);

  // Add effect to log when fetchAllUserList completes
  useEffect(() => {
    // console.log('fetchAllUserList function reference:', fetchAllUserList);
  }, [fetchAllUserList]);

  // If Schedule Report should be shown or not - for JST timezone
  const shouldShowScheduleReport = () => {
    // Get current user's email from props or localStorage
    const currentUserEmail = email || localStorage.getItem('userEmail');

    // console.log('shouldShowScheduleReport - currentUserEmail:', currentUserEmail);
    // console.log('shouldShowScheduleReport - allUserList type:', typeof allUserList);
    // console.log('shouldShowScheduleReport - allUserList length:', allUserList?.length || 0);
    // console.log('shouldShowScheduleReport - localStorage user_role:', localStorage.getItem('user_role'));

    // Handle allUserList regardless of its format
    let userList = [];

    if (Array.isArray(allUserList)) {
      // Already an array, use directly
      userList = allUserList;
      console.log('allUserList is already an array, length:', userList.length);
    } else if (typeof allUserList === 'string') {
      // Try to parse string, but handle NaN values
      try {
        // Replace NaN values with null before parsing
        const cleanedString = allUserList.replace(/:\s*NaN\s*([,}])/g, ': null$1');
        userList = JSON.parse(cleanedString);
        console.log('Parsed allUserList from string (after cleaning):', userList?.length || 0);
      } catch (e) {
        console.error('Failed to parse allUserList even after cleaning:', e);
        userList = [];
      }
    } else if (allUserList && typeof allUserList === 'object') {
      // If it's an object but not an array, try to extract array from it
      if (allUserList.data && Array.isArray(allUserList.data)) {
        userList = allUserList.data;
      } else if (allUserList.users && Array.isArray(allUserList.users)) {
        userList = allUserList.users;
      } else {
        userList = [];
      }
      console.log('Extracted array from object, length:', userList.length);
    }

    // If allUserList is not loaded yet or empty, use localStorage check only
    if (!userList || userList.length === 0) {
      const hasValidRole = ['internal', 'admin'].includes(localStorage.getItem('user_role'));
      console.log('allUserList not loaded/empty, using localStorage only - result:', hasValidRole);
      return hasValidRole;
    }

    // Find current user in allUserList
    const currentUser = userList.find((user) => user && user.email_id === currentUserEmail);

    console.log('shouldShowScheduleReport - currentUser found:', !!currentUser);

    if (!currentUser) {
      console.log('Current user not found in allUserList, using localStorage only');
      const hasValidRole = ['internal', 'admin'].includes(localStorage.getItem('user_role'));
      console.log('localStorage fallback result:', hasValidRole);
      return hasValidRole;
    }

    // Check if user role is internal or admin (check both localStorage and user data)
    const localStorageRole = localStorage.getItem('user_role');
    const userDataRole = currentUser.role;
    const hasValidRole = ['internal', 'admin'].includes(localStorageRole) || ['internal', 'admin'].includes(userDataRole);

    // Check if org_utc is not empty and doesn't contain only "NA"
    const hasValidOrgUtc =
      currentUser.org_utc &&
      Array.isArray(currentUser.org_utc) &&
      currentUser.org_utc.length > 0 &&
      !(currentUser.org_utc.length === 1 && currentUser.org_utc[0] === 'NA');

    // console.log('shouldShowScheduleReport - localStorageRole:', localStorageRole);
    // console.log('shouldShowScheduleReport - userDataRole:', userDataRole);
    // console.log('shouldShowScheduleReport - hasValidRole:', hasValidRole);
    // console.log('shouldShowScheduleReport - org_utc:', currentUser.org_utc);
    // console.log('shouldShowScheduleReport - hasValidOrgUtc:', hasValidOrgUtc);

    const result = hasValidRole && hasValidOrgUtc;
    console.log('shouldShowScheduleReport - final result:', {
      userDataRole,
      hasValidRole,
      org_utc: currentUser.org_utc,
      hasValidOrgUtc,
      result
    });

    return result;
  };

  const anchorRef = useRef(null);
  const handleLogout = async () => {
    console.log('Logout');
    localStorage.clear();
    navigate('/', { replace: true });
    logoutUser();
  };

  const openClientOnboard = () => {
    navigate('/pages/clientonboard', { replace: true });
    setOpen(false);
  };

  const openScheduler = () => {
    navigate('/pages/schedulereport', { replace: true });
    setOpen(false);
  };

  const openAdvertiserList = () => {
    navigate('/advertisers', { replace: true });
    setOpen(false);
  };

  const handleClose = (event) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }
    setOpen(false);
  };

  const handleListItemClick = (event, index, route = '') => {
    setSelectedIndex(index);
    handleClose(event);

    if (route && route !== '') {
      navigate(route);
    }
  };

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClientOnboardCloseModal = (value) => {
    setShowClientOnboard(value);
  };

  const handleSchedulerCloseModal = (value) => {
    setShowSchedulerReport(value);
  };

  const prevOpen = useRef(open);
  useEffect(() => {
    if (prevOpen.current === true && open === false) {
      anchorRef.current.focus();
    }
    prevOpen.current = open;
  }, [open]);

  useEffect(() => {
    console.log(authorized, login);
    if (authorized == false && login == false) {
      navigate('/', { replace: true });
    }
  }, [authorized, login]);

  return (
    <>
      <Chip
        sx={{
          height: '48px',
          alignItems: 'center',
          borderRadius: '27px',
          transition: 'all .2s ease-in-out',
          borderColor: theme.palette.primary.light,
          backgroundColor: theme.palette.primary.light,
          '&[aria-controls="menu-list-grow"], &:hover': {
            borderColor: theme.palette.primary.main,
            background: `${theme.palette.primary.main}!important`,
            color: theme.palette.primary.light,
            '& svg': {
              stroke: theme.palette.primary.light
            }
          },
          '& .MuiChip-label': {
            lineHeight: 0
          }
        }}
        icon={
          <Avatar
            sx={{
              ...theme.typography.mediumAvatar,
              margin: '8px 0 8px 8px !important',
              cursor: 'pointer'
            }}
            ref={anchorRef}
            aria-controls={open ? 'menu-list-grow' : undefined}
            aria-haspopup="true"
            color="inherit"
          >
            {userName
              .split(' ')
              .slice(0, 2)
              .map((name) => name[0])
              .join('')}
          </Avatar>
        }
        label={<IconUserCircle stroke={1.5} size="2rem" color={theme.palette.primary.main} />}
        variant="outlined"
        ref={anchorRef}
        aria-controls={open ? 'menu-list-grow' : undefined}
        aria-haspopup="true"
        onClick={handleToggle}
        color="primary"
      />
      <Popper
        placement="bottom-end"
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal
        popperOptions={{
          modifiers: [
            {
              name: 'offset',
              options: {
                offset: [0, 14]
              }
            }
          ]
        }}
      >
        {({ TransitionProps }) => (
          <Transitions in={open} {...TransitionProps}>
            <Paper>
              <ClickAwayListener onClickAway={handleClose}>
                <MainCard border={false} elevation={16} content={false} boxShadow shadow={theme.shadows[16]}>
                  <Box sx={{ p: 2 }}>
                    <Stack>
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <Typography variant="h4">Welcome</Typography>
                        <br />
                        <Typography component="span" variant="h4" sx={{ fontWeight: 400 }}>
                          {userName}
                        </Typography>
                      </Stack>
                      <Typography variant="subtitle2">{email}</Typography>
                    </Stack>
                  </Box>
                  <PerfectScrollbar
                    style={{
                      height: '100%',
                      maxHeight: 'calc(100vh - 250px)',
                      overflowX: 'hidden'
                    }}
                  >
                    <Box sx={{ p: 2 }}>
                      <Divider />
                      <List
                        component="nav"
                        sx={{
                          width: '100%',
                          maxWidth: 350,
                          minWidth: 300,
                          backgroundColor: theme.palette.background.paper,
                          borderRadius: '10px',
                          [theme.breakpoints.down('md')]: {
                            minWidth: '100%'
                          },
                          '& .MuiListItemButton-root': {
                            mt: 0.5
                          }
                        }}
                      >
                        {/* Configuer Dashboard */}
                        {['internal', 'admin'].includes(localStorage.getItem('user_role')) && (
                          <ListItemButton
                            sx={{
                              borderRadius: `${customization.borderRadius}px`
                            }}
                            selected={selectedIndex === 4}
                            onClick={() => {
                              openClientOnboard();
                            }}
                          >
                            <ListItemIcon>
                              <IconUser stroke={1.5} size="1.3rem" />
                            </ListItemIcon>
                            <ListItemText primary={<Typography variant="body2">Configure Dashboard</Typography>} />
                          </ListItemButton>
                        )}

                        {/* Auto Email Scheduler - Updated condition Role fetched from shouldShowScheduleReport()*/}
                        {shouldShowScheduleReport() && (
                          <ListItemButton
                            sx={{
                              borderRadius: `${customization.borderRadius}px`
                            }}
                            selected={selectedIndex === 4}
                            onClick={() => {
                              openScheduler();
                            }}
                          >
                            <ListItemIcon>
                              <IconReport stroke={1.5} size="1.3rem" />
                            </ListItemIcon>
                            <ListItemText primary={<Typography variant="body2">Schedule Report</Typography>} />
                          </ListItemButton>
                        )}

                        {/* Back to Advertiser List */}
                        {['internal', 'admin'].includes(localStorage.getItem('user_role')) && (
                          <ListItemButton
                            sx={{
                              borderRadius: `${customization.borderRadius}px`
                            }}
                            selected={selectedIndex === 4}
                            onClick={() => {
                              openAdvertiserList();
                            }}
                          >
                            <ListItemIcon>
                              <IconArrowBack stroke={1.5} size="1.3rem" />
                            </ListItemIcon>
                            <ListItemText primary={<Typography variant="body2">Back to Advertiser Lists</Typography>} />
                          </ListItemButton>
                        )}

                        <ListItemButton
                          sx={{
                            borderRadius: `${customization.borderRadius}px`
                          }}
                          selected={selectedIndex === 4}
                          onClick={handleLogout}
                        >
                          <ListItemIcon>
                            <IconLogout stroke={1.5} size="1.3rem" />
                          </ListItemIcon>
                          <ListItemText primary={<Typography variant="body2">Logout</Typography>} />
                        </ListItemButton>
                      </List>
                    </Box>
                  </PerfectScrollbar>
                </MainCard>
              </ClickAwayListener>
            </Paper>
          </Transitions>
        )}
      </Popper>

      {showClientOnboard && (
        <div style={{ position: 'fixed', top: '20%', left: '30%', background: 'white', zIndex: 9999 }}>
          <ScheduleReport showModal={showClientOnboard} closeModal={handleClientOnboardCloseModal} />
        </div>
      )}

      {showSchedulerReport && (
        <div style={{ position: 'fixed', top: '20%', left: '30%', background: 'white', zIndex: 9999 }}>
          <ScheduleReport showModal={showSchedulerReport} closeModal={handleSchedulerCloseModal} />
        </div>
      )}
    </>
  );
};

const mapStateToProps = (state) => {
  // Debug logging to check Redux state
  // console.log('mapStateToProps - full advertiserReducer state:', state.advertiserReducer);
  // console.log('mapStateToProps - all_dashboard_user_list:', state.advertiserReducer.all_dashboard_user_list);

  return {
    email: state.userReducer.userEmail,
    user_role: state.advertiserReducer.user_type,
    userName: state.userReducer.userName,
    authorized: state.userReducer.authorized,
    login: state.userReducer.login,
    timeZone: state.advertiserReducer.timeZone,
    allUserList: state.advertiserReducer.all_dashboard_user_list
  };
};

const mapDispatchToProps = {
  fetchUserDetails: fetchUserDetails,
  fetchAllUserList: fetchAllUserList,
  fetchUserRoleByEmailId: fetchUserRoleByEmailId,
  logoutUser: logoutUser
};

export default connect(mapStateToProps, mapDispatchToProps)(ProfileSection);
